import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { InvoiceAccess } from '../types/invoice';
import invoiceService from '../services/invoiceService';

interface InvoiceAccessContextValue {
  access: InvoiceAccess | null;
  loading: boolean;
  error: string | null;
  refreshAccess: () => Promise<void>;
}

const InvoiceAccessContext = createContext<InvoiceAccessContextValue | undefined>(undefined);

interface InvoiceAccessProviderProps {
  children: ReactNode;
}

export const InvoiceAccessProvider: React.FC<InvoiceAccessProviderProps> = ({ children }) => {
  const [access, setAccess] = useState<InvoiceAccess | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadAccess = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await invoiceService.getAccess();
      setAccess(response.data);
    } catch (err: any) {
      console.error('Failed to load invoice access:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load invoice access');
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const value: InvoiceAccessContextValue = {
    access,
    loading,
    error,
    refreshAccess: loadAccess,
  };

  return <InvoiceAccessContext.Provider value={value}>{children}</InvoiceAccessContext.Provider>;
};

export const useInvoiceAccess = (): InvoiceAccessContextValue => {
  const context = useContext(InvoiceAccessContext);
  if (context === undefined) {
    throw new Error('useInvoiceAccess must be used within an InvoiceAccessProvider');
  }
  return context;
};
